import { getHeaders, getHeadersPdf } from "@/helpers/token/CaptureToken";
import { authLogger } from "@/config/logger";
import axios, { AxiosError, AxiosResponse } from "axios";

const TIMEOUT_MS = 15000;
const API_ESCUELA_BASE_URL = process.env.NEXT_PUBLIC_API_ESCUELA;

// NEXT_PUBLIC_API_ESCUELA se inlinea en build time. Si el bundle de cliente se compiló
// sin esa variable presente, quedaba "undefined" horneado y `undefined + url` generaba
// una URL relativa que el navegador resolvía contra la página actual en vez de la API.
function buildUrl(url: string): string {
    if (!API_ESCUELA_BASE_URL) {
        authLogger.apiConfigError(url);
        throw {
            error: true,
            type: "CONFIG_ERROR",
            message: "NEXT_PUBLIC_API_ESCUELA no está configurada. Verifique las variables de entorno del despliegue.",
        };
    }
    return API_ESCUELA_BASE_URL + url;
}

function handleApiError(url: string, error: unknown): never {
    if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<any>;

        if (!axiosError.response) {
            // Sin respuesta: timeout, offline, DNS
            authLogger.apiNetworkError(url, axiosError.code);
            throw {
                error: true,
                type: "NETWORK_ERROR",
                message: "Sin conexión. Verifique su internet e intente nuevamente.",
                code: axiosError.code,
            };
        }

        const status = axiosError.response.status;

        if (status === 401 || status === 403) {
            authLogger.apiAuthError(url, status);
        } else if (status >= 500) {
            authLogger.apiServerError(url, status);
        }

        // Rethrow el AxiosError original para que los actions existentes
        // puedan seguir haciendo: return axiosError?.response?.data
        throw error;
    }

    throw error;
}

export const getApi = async (url: string, params?: any) => {
    const headers = await getHeaders();
    try {
        const response: AxiosResponse = await axios.get(buildUrl(url), { headers, params, timeout: TIMEOUT_MS });
        return response.data;
    } catch (error) {
        handleApiError(url, error);
    }
}

export const postApi = async (url: string, data: any) => {
    const headers = await getHeaders();
    try {
        const response: AxiosResponse = await axios.post(buildUrl(url), data, { headers, timeout: TIMEOUT_MS });
        return response.data;
    } catch (error) {
        handleApiError(url, error);
    }
}

export const putApi = async (url: string, data: any) => {
    const headers = await getHeaders();
    try {
        const response: AxiosResponse = await axios.put(buildUrl(url), data, { headers, timeout: TIMEOUT_MS });
        return response.data;
    } catch (error) {
        handleApiError(url, error);
    }
}

export const patchApi = async (url: string, data: any) => {
    const headers = await getHeaders();
    try {
        const response: AxiosResponse = await axios.patch(buildUrl(url), data, { headers, timeout: TIMEOUT_MS });
        return response.data;
    } catch (error) {
        handleApiError(url, error);
    }
}

export const deleteApi = async (url: string, id: any) => {
    const headers = await getHeaders();
    try {
        const response: AxiosResponse = await axios.delete(buildUrl(url), {
            headers: headers,
            data: id,
            timeout: TIMEOUT_MS,
        });
        return response.data;
    } catch (error) {
        handleApiError(url, error);
    }
}

export const postApiParams = async (url: string, data: any, params: any) => {
    const headers = await getHeaders();
    try {
        const response: AxiosResponse = await axios.post(buildUrl(url), data, { headers, params, timeout: TIMEOUT_MS });
        return response.data;
    } catch (error) {
        handleApiError(url, error);
    }
}

export const getApiPdf = async (url: string, params?: any, timeout: number = TIMEOUT_MS) => {
    const headers = await getHeadersPdf();
    try {
        const response: AxiosResponse = await axios.get(buildUrl(url), { headers, params, responseType: "blob", timeout });
        return response.data;
    } catch (error) {
        handleApiError(url, error);
    }
}
